import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import axiosInstance from '../../../config/axios.config';
import ProtectedContent from '../../ProtectedContent';
import { scoreBand, SCORE_MAX } from './creditScore';

const STATUS_MAP = {
    'Current Account': { severity: 'success', label: 'Current' },
    Closed: { severity: 'info', label: 'Closed' },
    Overdue: { severity: 'warning', label: 'Overdue' },
    'Written Off': { severity: 'danger', label: 'Written Off' },
    Settled: { severity: 'warning', label: 'Settled' },
    STD: { severity: 'success', label: 'Standard' },
    SUB: { severity: 'warning', label: 'Sub-Standard' },
    DBT: { severity: 'danger', label: 'Doubtful' },
    LSS: { severity: 'danger', label: 'Loss' },
    SMA: { severity: 'warning', label: 'Special Mention' },
};

const OWNERSHIP_MAP = {
    Individual: { icon: 'pi-user', short: 'IND' },
    Joint: { icon: 'pi-users', short: 'JNT' },
    Guarantor: { icon: 'pi-shield', short: 'GTR' },
    'Authorized User': { icon: 'pi-check', short: 'AUTH' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const inr = (v) => `₹${(parseFloat(v) || 0).toLocaleString('en-IN')}`;

const getValue = (data) => {
    if (data === null || data === undefined) return 'N/A';
    if (typeof data === 'string' || typeof data === 'number') return data;
    if (typeof data === 'object') {
        return data.value || data.serial_number || data.reported_date || data.name || data.type || 'N/A';
    }
    return String(data);
};

// scoreSeverity() lived here. Both call sites now go through scoreBand(), which
// applies these same thresholds AND distinguishes "no score returned" from a
// score of zero — the distinction this function could not make, because it took
// a number that `|| 0` had already fabricated (GH #405).

const paymentStatusInfo = (status) => {
    if (!status) return { label: 'N/A', severity: 'info' };
    if (status === '000') return { label: 'On Time', severity: 'success' };
    if (status === 'NEW') return { label: 'New Account', severity: 'info' };
    if (status === 'STD') return { label: 'Standard', severity: 'success' };
    if (status === 'SUB') return { label: 'Sub-Standard', severity: 'warning' };
    if (status === 'DBT' || status === 'LSS') return { label: status === 'DBT' ? 'Doubtful' : 'Loss', severity: 'danger' };
    if (/\d+/.test(status)) {
        const days = parseInt(status, 10);
        return { label: `${days}+ days late`, severity: days >= 90 ? 'danger' : 'warning' };
    }
    return { label: status, severity: 'info' };
};

const SectionLabel = ({ icon, children }) => (
    <div className="flex align-items-center gap-2 mb-3">
        <i className={`pi ${icon} text-primary`} aria-hidden="true" />
        <span className="text-sm font-semibold text-900 uppercase">{children}</span>
    </div>
);

const KpiCell = ({ icon, label, value, valueClass = 'text-900' }) => (
    <div className="surface-card border-1 surface-border border-round p-3 h-full">
        <div className="flex align-items-center justify-content-between mb-2">
            <span className="text-500 text-xs font-medium uppercase">{label}</span>
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
        </div>
        <div className={`text-base font-bold ${valueClass}`}>{value}</div>
    </div>
);

const CreditScoreCard = ({ scoreData }) => {
    if (!scoreData) return null;
    // The `if (!scoreData)` guard above only catches a wholly absent object; a
    // payload like `{ name: 'Equifax Score' }` walked past it and
    // `parseInt(… || 0, 10)` graded the borrower 0 / "Poor" on a 0% gauge
    // (GH #405, same defect as Experian). scoreBand tells absent from bad.
    const band = scoreBand(scoreData.value ?? scoreData.score);
    return (
        <Card className="h-full">
            <div className="flex flex-column align-items-center text-center">
                <span className="text-xs text-600 mb-1">{scoreData.name || scoreData.type || 'Credit Score'}</span>
                <span className={`text-xl font-bold ${band.color} mb-2`}>{band.display}</span>
                {/* No gauge without a score — a 0%-filled bar is itself a reading. */}
                {band.available ? (
                    <ProgressBar
                        value={Math.round(band.pct)}
                        showValue={false}
                        className="w-full mb-2"
                        style={{ height: '0.5rem' }}
                        aria-label={`Credit score ${band.score} of ${SCORE_MAX}`}
                    />
                ) : null}
                <Tag severity={band.severity} value={band.label} className="text-xs" />
                {scoreData.version && (
                    <span className="text-xs text-500 mt-2">Version {scoreData.version}</span>
                )}
            </div>
        </Card>
    );
};

const ScoringFactors = ({ scores }) => {
    const haveAny = scores.some((s) => s.scoring_elements && s.scoring_elements.length > 0);
    if (!haveAny) return null;
    return (
        <Card className="h-full">
            <SectionLabel icon="pi-info-circle">Scoring Factors</SectionLabel>
            {scores.map((score, i) =>
                score.scoring_elements && score.scoring_elements.length > 0 ? (
                    <div key={i} className="mb-3">
                        {score.name && <p className="text-sm font-semibold text-900 m-0 mb-2">{score.name}</p>}
                        {score.scoring_elements.slice(0, 6).map((elem, j) => (
                            <div key={j} className="flex align-items-center justify-content-between surface-100 border-round p-2 mb-1 gap-2">
                                <span className="text-xs text-700 flex-1">{elem.description}</span>
                                <Badge value={elem.code} severity="info" />
                            </div>
                        ))}
                        {score.scoring_elements.length > 6 && (
                            <p className="text-xs text-600 text-center m-0 mt-1">
                                +{score.scoring_elements.length - 6} more factors
                            </p>
                        )}
                    </div>
                ) : null
            )}
        </Card>
    );
};

const renderOwnership = (type) => {
    const cfg = OWNERSHIP_MAP[type] || { icon: 'pi-question', short: (type || 'N/A').slice(0, 3).toUpperCase() };
    return (
        <span className="flex align-items-center gap-1 text-sm">
            <i className={`pi ${cfg.icon} text-600`} aria-hidden="true" />
            <span className="font-medium">{cfg.short}</span>
        </span>
    );
};

const PaymentHistoryView = ({ historyData }) => {
    if (!historyData || historyData.length === 0) return null;

    const yearGroups = historyData.reduce((acc, item) => {
        const yearMatch = item.key?.match(/(\d{4})/);
        const year = yearMatch ? yearMatch[1] : 'Other';
        (acc[year] = acc[year] || []).push(item);
        return acc;
    }, {});
    const sortedYears = Object.keys(yearGroups).sort((a, b) => b.localeCompare(a));

    const onTime = historyData.filter((i) => i.payment_status === '000' || i.payment_status === 'STD').length;
    const defaults = historyData.filter((i) => {
        const s = i.payment_status || '';
        return s.includes('90') || s.includes('120') || s === 'DBT' || s === 'LSS';
    }).length;
    const late = historyData.length - onTime - defaults;

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-3">
                <Tag severity="success" value="On Time" />
                <Tag severity="info" value="New" />
                <Tag severity="warning" value="Late" />
                <Tag severity="danger" value="Default" />
            </div>

            <Card className="mb-3">
                <SectionLabel icon="pi-history">Payment History</SectionLabel>
                {sortedYears.map((year) => {
                    const yearData = yearGroups[year].sort((a, b) => {
                        const ma = parseInt(a.key?.split('-')[1] || '0', 10);
                        const mb = parseInt(b.key?.split('-')[1] || '0', 10);
                        return ma - mb;
                    });
                    return (
                        <div key={year} className="mb-3">
                            <Divider align="left" className="my-2">
                                <span className="text-sm font-semibold text-700">{year}</span>
                            </Divider>
                            <div className="flex flex-wrap gap-2">
                                {yearData.map((item, idx) => {
                                    const date = new Date(item.key);
                                    const month = isNaN(date.getMonth()) ? '—' : MONTHS[date.getMonth()];
                                    const info = paymentStatusInfo(item.payment_status);
                                    return (
                                        <div key={idx} className="flex flex-column align-items-center text-center">
                                            <span className="text-xs text-600 font-medium">{month}</span>
                                            <Tag severity={info.severity} value={item.payment_status} className="text-xs mt-1" />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </Card>

            <Card>
                <SectionLabel icon="pi-chart-bar">Summary</SectionLabel>
                <div className="grid">
                    <div className="col-4 text-center">
                        <i className="pi pi-check-circle text-green-500 pi-2x" aria-hidden="true" />
                        <div className="text-base font-bold text-green-600 mt-2">{onTime}</div>
                        <p className="text-xs text-600 m-0">On time</p>
                    </div>
                    <div className="col-4 text-center">
                        <i className="pi pi-exclamation-triangle text-orange-500 pi-2x" aria-hidden="true" />
                        <div className="text-base font-bold text-orange-600 mt-2">{late}</div>
                        <p className="text-xs text-600 m-0">Late</p>
                    </div>
                    <div className="col-4 text-center">
                        <i className="pi pi-times-circle text-red-500 pi-2x" aria-hidden="true" />
                        <div className="text-base font-bold text-red-600 mt-2">{defaults}</div>
                        <p className="text-xs text-600 m-0">Defaults</p>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const AccountsTable = ({ accounts, onViewHistory }) => {
    if (!accounts || accounts.length === 0) {
        return <Message severity="info" text="No accounts found" className="w-full" />;
    }

    const accountStatusBody = (row) => {
        const cfg = STATUS_MAP[row.account_status] || { severity: 'info', label: row.account_status || 'N/A' };
        return <Tag severity={cfg.severity} value={cfg.label} className="text-xs" />;
    };

    const balanceBody = (row) => (
        <span className="font-semibold text-900">{inr(row.balance)}</span>
    );

    const pastDueBody = (row) => {
        const due = parseFloat(row.past_due_amount) || 0;
        return (
            <span className={due > 0 ? 'text-red-500 font-semibold' : 'text-green-600 font-semibold'}>
                {inr(due)}
            </span>
        );
    };

    const historyBody = (row) =>
        row.last_four_year_history_data && row.last_four_year_history_data.length > 0 ? (
            <Button
                icon="pi pi-eye"
                rounded
                text
                size="small"
                aria-label={`View history for ${row.account_number}`}
                onClick={() => onViewHistory(row)}
            />
        ) : (
            <span className="text-500">—</span>
        );

    return (
        <DataTable
            value={accounts}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
            paginatorClassName="text-sm"
            pageLinkSize={3}
            size="small"
            stripedRows
            showGridlines
            scrollable
            scrollHeight="flex"
            className="p-datatable-sm p-datatable-gridlines"
            responsiveLayout="scroll"
            emptyMessage={<div className="text-center text-600 text-sm p-3">No accounts found</div>}
        >
            <Column field="account_number" header="Account" sortable frozen alignFrozen="left" style={{ minWidth: '160px' }} />
            <Column field="institution" header="Bank" sortable style={{ minWidth: '160px' }} />
            <Column field="account_type" header="Type" sortable style={{ minWidth: '120px' }} />
            <Column field="ownership_type" header="Owner" body={(row) => renderOwnership(row.ownership_type)} style={{ minWidth: '100px' }} />
            <Column field="account_status" header="Status" body={accountStatusBody} style={{ minWidth: '120px' }} />
            <Column field="date_opened" header="Since" sortable style={{ minWidth: '110px' }} />
            <Column field="balance" header="Balance" sortable body={balanceBody} style={{ minWidth: '120px' }} />
            <Column field="past_due_amount" header="Past due" sortable body={pastDueBody} style={{ minWidth: '120px' }} />
            <Column header="History" body={historyBody} style={{ minWidth: '90px', textAlign: 'center' }} />
        </DataTable>
    );
};

const EnquiryTable = ({ enquiries }) => {
    if (!enquiries || enquiries.length === 0) {
        return <Message severity="info" text="No enquiries found" className="w-full" />;
    }
    return (
        <DataTable
            value={enquiries}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
            paginatorClassName="text-sm"
            pageLinkSize={3}
            size="small"
            stripedRows
            showGridlines
            scrollable
            scrollHeight="flex"
            className="p-datatable-sm p-datatable-gridlines"
            responsiveLayout="scroll"
        >
            <Column field="institution" header="Bank / NBFC" sortable />
            <Column field="date" header="Date" sortable />
            <Column field="purpose" header="Purpose" sortable />
            <Column field="amount" header="Amount" body={(row) => <span className="font-semibold">{inr(row.amount)}</span>} sortable />
        </DataTable>
    );
};

const DocCard = ({ icon, title, subtitle, items }) => (
    <Card className="h-full">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary text-lg`} aria-hidden="true" />
            <div>
                <h3 className="text-base font-semibold text-900 m-0">{title}</h3>
                <p className="text-xs text-500 m-0">{subtitle}</p>
            </div>
        </div>
        <div className="flex flex-column gap-2">
            {items.map((item, i) => (
                <div key={i} className="surface-100 border-round p-2 flex align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 min-w-0">
                        <i className="pi pi-hashtag text-600" aria-hidden="true" />
                        <span className="text-sm font-semibold text-900 font-mono">{item.value}</span>
                    </div>
                    <Tag severity="success" value="Verified" className="text-xs" />
                </div>
            ))}
        </div>
    </Card>
);

const PhoneCard = ({ phone }) => (
    <Card>
        <div className="flex align-items-center justify-content-between gap-2">
            <div className="flex align-items-center gap-2 min-w-0">
                <i className="pi pi-mobile text-primary" aria-hidden="true" />
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-900 m-0 font-mono">{phone.value}</p>
                    {phone.reported_date && (
                        <p className="text-xs text-500 m-0 mt-1">
                            <i className="pi pi-clock mr-1" aria-hidden="true" />
                            {phone.reported_date}
                        </p>
                    )}
                </div>
            </div>
            <Tag severity="success" value="Active" className="text-xs" />
        </div>
    </Card>
);

const EmailCard = ({ email }) => (
    <Card>
        <div className="flex align-items-center gap-2 min-w-0">
            <i className="pi pi-envelope text-primary" aria-hidden="true" />
            <p className="text-sm font-semibold text-900 m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1">
                {email.value}
            </p>
        </div>
    </Card>
);

const AddressCard = ({ address }) => (
    <Card>
        <div className="flex align-items-start gap-2">
            <i className="pi pi-home text-primary mt-1" aria-hidden="true" />
            <div className="flex-1">
                <div className="flex align-items-start justify-content-between gap-2 mb-2">
                    <p className="text-sm font-semibold text-900 m-0 line-height-3">{address.detailed_address}</p>
                    {address.type && (
                        <Tag
                            severity={address.type?.toLowerCase().includes('current') ? 'success' : 'info'}
                            value={address.type}
                            className="text-xs"
                        />
                    )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-600">
                    {address.state && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-map" aria-hidden="true" />
                            {address.state}
                        </span>
                    )}
                    {address.pincode && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-inbox" aria-hidden="true" />
                            {address.pincode}
                        </span>
                    )}
                    {address.date_of_reporting && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-calendar" aria-hidden="true" />
                            {address.date_of_reporting}
                        </span>
                    )}
                </div>
            </div>
        </div>
    </Card>
);

const tabHeader = (icon, label, count) => (
    <span className="flex align-items-center gap-2">
        <i className={`pi ${icon}`} aria-hidden="true" />
        <span className="font-medium">{label}</span>
        {count !== undefined && count !== null && (
            <Badge value={count} severity="info" className="ml-1" />
        )}
    </span>
);

const EquifaxOutputComponent = ({
    reportData,
    onDownloadPDF,
    pdfDownloadUrl,
    serviceDetails,
    actualServiceDetails,
    hideCredits,
    isSharedView,
}) => {
    const effectiveServiceInfo = actualServiceDetails || serviceDetails;
    const shouldHideCredits = hideCredits || isSharedView;
    const toast = useRef(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [paymentHistoryModal, setPaymentHistoryModal] = useState(false);
    const [selectedAccountHistory, setSelectedAccountHistory] = useState(null);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);

    let parsedData = typeof reportData === 'string'
        ? (() => { try { return JSON.parse(reportData); } catch (_) { return reportData; } })()
        : reportData;
    if (parsedData?.report_json) parsedData = parsedData.report_json;
    else if (parsedData?.api_response) parsedData = parsedData.api_response;

    const remainingBalance = parsedData?.data?.remaining_balance ?? parsedData?.remaining_balance ?? effectiveServiceInfo?.available_credits;
    const pdfPasswordHint =
        parsedData?.pdf_password_hint ||
        reportData?.pdf_password_hint ||
        reportData?.report_json?.pdf_password_hint ||
        parsedData?.data?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        null;

    const hasError = parsedData?.success === false || parsedData?.error;
    const isNoRecordsFound = parsedData?.code === '1004';

    const handleDownloadPDF = async () => {
        if (pdfPasswordHint) {
            toast.current?.show({
                severity: 'info',
                summary: 'PDF password hint',
                detail: pdfPasswordHint,
                life: 8000,
            });
        }
        if (typeof onDownloadPDF === 'function') {
            onDownloadPDF();
            return;
        }
        const downloadLink =
            pdfDownloadUrl ||
            reportData?.pdf_download_url ||
            reportData?.download_link ||
            reportData?.download_url ||
            reportData?.data?.pdf_download_url ||
            reportData?.data?.download_link;
        if (!downloadLink) {
            toast.current?.show({ severity: 'warn', summary: 'PDF unavailable', detail: 'Download URL not available', life: 3000 });
            return;
        }
        if (downloadLink.startsWith('http')) {
            window.open(downloadLink, '_blank');
            return;
        }
        try {
            const urlObj = new URL(downloadLink, window.location.origin);
            const res = await axiosInstance.get(urlObj.pathname, {
                responseType: 'blob',
                headers: { Accept: 'application/pdf' },
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `equifax_report_${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.current?.show({ severity: 'success', summary: 'Downloaded', detail: 'PDF saved', life: 2000 });
        } catch (error) {
            console.error('Equifax PDF download failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const detail =
                status === 404 ? 'Report not found. Please regenerate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                status === 500 ? 'Internal server error. Please try again later.' :
                error.response?.data?.message || 'PDF download failed';
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail, life: 3500 });
        }
    };

    if (!reportData) {
        return <Message severity="info" text="No report data available." className="w-full" />;
    }

    if (hasError) {
        return (
            <Card className="text-center">
                <Toast ref={toast} />
                <i className="pi pi-times-circle pi-3x text-red-500 mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">Report generation failed</h3>
                <p className="text-600 text-sm m-0 mb-3">
                    {parsedData?.message || 'An error occurred while generating the report. Please try again.'}
                </p>
                <Button label="Try again" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={() => window.location.reload()} />
            </Card>
        );
    }

    if (isNoRecordsFound) {
        return (
            <Card className="text-center">
                <Toast ref={toast} />
                <i className="pi pi-search pi-3x text-orange-500 mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">No records found</h3>
                <p className="text-600 text-sm m-0">No credit bureau records were found for the provided details.</p>
            </Card>
        );
    }

    const profileData =
        parsedData?.data?.data?.profile_data ||
        parsedData?.data?.profile_data ||
        parsedData?.profile_data ||
        parsedData;

    if (!profileData) {
        return (
            <Card className="text-center">
                <Toast ref={toast} />
                <Message severity="warn" text="No profile data available for display." />
            </Card>
        );
    }

    const handleViewHistory = (row) => {
        setSelectedAccountHistory(row.last_four_year_history_data);
        setSelectedAccountNumber(row.account_number);
        setPaymentHistoryModal(true);
    };

    const scores = Array.isArray(profileData.score_detail) ? profileData.score_detail : profileData.score_detail ? [profileData.score_detail] : [];
    const accountSummary = profileData.account_summary;
    const accountDetail = profileData.account_detail || [];
    const nationalDocs = profileData.national_document_data || {};
    const personal = profileData.personal_information;
    const phones = profileData.phone || [];
    const emails = profileData.email || [];
    const addresses = profileData.address || [];
    const enquiries = profileData.enquiry || [];
    const docCount =
        (nationalDocs.pan?.length || 0) +
        (nationalDocs.aadhaar?.length || 0) +
        (nationalDocs.voter_id?.length || 0);

    // Bureau-grade hero KPIs — surface the most important numbers at a glance
    const primaryScore = scores[0];
    // The hero KPI carried the same `|| 0` coercion as CreditScoreCard, so a
    // score object with no value graded the borrower 0 / "Poor" at the very top
    // of the report (GH #405 — the issue named the card; this is the second
    // site). Hiding the tile is the right answer: a KPI strip is for facts that
    // exist, and "we do not have a score" is not a headline number.
    const primaryBand = primaryScore ? scoreBand(primaryScore.value ?? primaryScore.score) : null;
    const scoreInfo = primaryBand && primaryBand.available ? primaryBand : null;
    const primaryScoreNum = scoreInfo ? scoreInfo.score : null;
    const totalAccounts = accountSummary?.number_of_accounts || accountSummary?.total_accounts || 0;
    const pastDueCount = accountSummary?.number_of_past_due_accounts || accountSummary?.overdue_accounts || 0;
    const totalBalance = accountSummary?.total_balance_amount;

    return (
        <ProtectedContent type="report" title="Equifax Bureau Report">
            <Toast ref={toast} />

            {/* Page sub-header — h2 + remaining credits Tag + PDF action */}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                <div className="flex-1">
                    <h2 className="text-lg font-semibold text-900 m-0">
                        <i className="pi pi-chart-line mr-2 text-primary" aria-hidden="true" />
                        Equifax Credit Report
                    </h2>
                    {personal?.full_name && (
                        <p className="text-600 text-sm m-0 mt-1">
                            Report for <span className="text-900 font-medium">{personal.full_name}</span>
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap align-items-center gap-2">
                    {!shouldHideCredits && (
                        <Tag
                            severity="success"
                            icon="pi pi-wallet"
                            value={`Remaining: ${remainingBalance ?? 'N/A'}`}
                            className="text-sm"
                        />
                    )}
                    <Button
                        label="Download PDF"
                        icon="pi pi-download"
                        size="small"
                        outlined
                        onClick={handleDownloadPDF}
                    />
                </div>
            </div>

            {/* Hero KPI cards — bureau metrics surfaced above the TabView */}
            <div className="grid mb-3">
                {scoreInfo && (
                    <div className="col-12 md:col-6 lg:col-3">
                        <Card className="h-full shadow-1" style={{ borderLeft: `3px solid var(--${scoreInfo.severity === 'success' ? 'green' : scoreInfo.severity === 'warning' ? 'orange' : scoreInfo.severity === 'info' ? 'blue' : 'red'}-500)` }} pt={{ body: { className: 'p-3' } }}>
                            <div className="flex justify-content-between align-items-start">
                                <div className="flex flex-column gap-1">
                                    <span className="text-600 text-sm font-medium">{primaryScore.name || 'Credit Score'}</span>
                                    <span className={`text-xl font-bold ${scoreInfo.color}`}>{primaryScoreNum}</span>
                                </div>
                                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                                    <i className="pi pi-chart-line text-xl text-primary" aria-hidden="true" />
                                </div>
                            </div>
                            <div className="flex align-items-center justify-content-between mt-2">
                                <Tag severity={scoreInfo.severity} value={scoreInfo.label} className="text-xs" />
                                {primaryScore.version && <span className="text-500 text-xs">v{primaryScore.version}</span>}
                            </div>
                        </Card>
                    </div>
                )}
                <div className={scoreInfo ? 'col-6 md:col-6 lg:col-3' : 'col-6 md:col-6 lg:col-4'}>
                    <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                        <div className="flex justify-content-between align-items-start">
                            <div className="flex flex-column gap-1">
                                <span className="text-600 text-sm font-medium">Total Accounts</span>
                                <span className="text-xl font-bold text-900">{totalAccounts}</span>
                            </div>
                            <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                                <i className="pi pi-briefcase text-xl text-primary" aria-hidden="true" />
                            </div>
                        </div>
                        <span className="text-500 text-xs block mt-2">Across all reporting institutions</span>
                    </Card>
                </div>
                <div className={scoreInfo ? 'col-6 md:col-6 lg:col-3' : 'col-6 md:col-6 lg:col-4'}>
                    <Card className="h-full shadow-1" style={pastDueCount > 0 ? { borderLeft: '3px solid var(--orange-500)' } : undefined} pt={{ body: { className: 'p-3' } }}>
                        <div className="flex justify-content-between align-items-start">
                            <div className="flex flex-column gap-1">
                                <span className="text-600 text-sm font-medium">Past Due</span>
                                <span className={`text-xl font-bold ${pastDueCount > 0 ? 'text-orange-600' : 'text-900'}`}>{pastDueCount}</span>
                            </div>
                            <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                                <i className="pi pi-exclamation-triangle text-xl text-primary" aria-hidden="true" />
                            </div>
                        </div>
                        <span className="text-500 text-xs block mt-2">Accounts with overdue payments</span>
                    </Card>
                </div>
                {totalBalance != null && (
                    <div className={scoreInfo ? 'col-12 md:col-6 lg:col-3' : 'col-12 md:col-12 lg:col-4'}>
                        <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                            <div className="flex justify-content-between align-items-start">
                                <div className="flex flex-column gap-1">
                                    <span className="text-600 text-sm font-medium">Total Outstanding</span>
                                    <span className="text-xl font-bold text-900">{inr(totalBalance)}</span>
                                </div>
                                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                                    <i className="pi pi-wallet text-xl text-primary" aria-hidden="true" />
                                </div>
                            </div>
                            <span className="text-500 text-xs block mt-2">Sum of balances across accounts</span>
                        </Card>
                    </div>
                )}
            </div>

            <Card>
                <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
                    {scores.length > 0 && (
                        <TabPanel header={tabHeader('pi-chart-line', 'Credit Score Details', scores[0]?.value)}>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="grid">
                                        {scores.map((score, i) => (
                                            <div key={i} className="col-12">
                                                <CreditScoreCard scoreData={score} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <ScoringFactors scores={scores} />
                                </div>
                            </div>
                        </TabPanel>
                    )}

                    {accountSummary && (
                        <TabPanel header={tabHeader('pi-wallet', 'Account Summary')}>
                            <div className="grid">
                                <div className="col-12 sm:col-4">
                                    <KpiCell
                                        icon="pi-briefcase"
                                        label="Total accounts"
                                        value={accountSummary.number_of_accounts || accountSummary.total_accounts || 0}
                                    />
                                </div>
                                <div className="col-12 sm:col-4">
                                    <KpiCell
                                        icon="pi-exclamation-triangle"
                                        label="Past due"
                                        value={accountSummary.number_of_past_due_accounts || accountSummary.overdue_accounts || 0}
                                        valueClass="text-orange-600"
                                    />
                                </div>
                                <div className="col-12 sm:col-4">
                                    <KpiCell
                                        icon="pi-check-circle"
                                        label="Zero balance"
                                        value={accountSummary.number_of_zero_balance_accounts || accountSummary.zero_balance_accounts || 0}
                                        valueClass="text-green-600"
                                    />
                                </div>
                            </div>
                            <div className="grid mt-2">
                                <div className="col-12 sm:col-6 lg:col-3">
                                    <KpiCell icon="pi-wallet" label="Total balance" value={inr(accountSummary.total_balance_amount)} />
                                </div>
                                <div className="col-12 sm:col-6 lg:col-3">
                                    <KpiCell icon="pi-exclamation-circle" label="Past due ₹" value={inr(accountSummary.total_past_due)} valueClass="text-red-500" />
                                </div>
                                <div className="col-12 sm:col-6 lg:col-3">
                                    <KpiCell icon="pi-credit-card" label="Sanction" value={inr(accountSummary.total_sanction_amount)} />
                                </div>
                                <div className="col-12 sm:col-6 lg:col-3">
                                    <KpiCell icon="pi-calendar" label="Monthly EMI" value={inr(accountSummary.total_monthly_payment_amount)} />
                                </div>
                                {accountSummary.single_highest_credit && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-arrow-up" label="Highest credit" value={inr(accountSummary.single_highest_credit)} />
                                    </div>
                                )}
                                {accountSummary.single_highest_sanction_amount && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-arrow-up-right" label="Highest sanction" value={inr(accountSummary.single_highest_sanction_amount)} />
                                    </div>
                                )}
                                {accountSummary.average_open_balance && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-equals" label="Avg open balance" value={inr(accountSummary.average_open_balance)} />
                                    </div>
                                )}
                                {accountSummary.number_of_active_accounts && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-bolt" label="Active accounts" value={accountSummary.number_of_active_accounts} />
                                    </div>
                                )}
                                {accountSummary.number_of_write_offs !== undefined && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <div className="surface-card border-1 surface-border border-round p-3 h-full">
                                            <div className="flex align-items-center justify-content-between mb-2">
                                                <span className="text-500 text-xs font-medium uppercase">Write-offs</span>
                                                <i className="pi pi-times-circle text-primary" aria-hidden="true" />
                                            </div>
                                            <Tag
                                                value={accountSummary.number_of_write_offs}
                                                severity={String(accountSummary.number_of_write_offs) === '0' ? 'success' : 'danger'}
                                                className="text-sm"
                                            />
                                        </div>
                                    </div>
                                )}
                                {accountSummary.recent_account && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-clock" label="Recent account" value={accountSummary.recent_account} />
                                    </div>
                                )}
                                {accountSummary.oldest_account && (
                                    <div className="col-12 sm:col-6 lg:col-3">
                                        <KpiCell icon="pi-history" label="Oldest account" value={accountSummary.oldest_account} />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                    )}

                    {accountDetail.length > 0 && (
                        <TabPanel header={tabHeader('pi-credit-card', 'Account Details', accountDetail.length)}>
                            <AccountsTable accounts={accountDetail} onViewHistory={handleViewHistory} />
                        </TabPanel>
                    )}

                    {(nationalDocs.pan || nationalDocs.aadhaar || nationalDocs.voter_id) && (
                        <TabPanel header={tabHeader('pi-id-card', 'National Documents', docCount)}>
                            <div className="grid">
                                {nationalDocs.pan && (
                                    <div className="col-12 md:col-6 lg:col-4">
                                        <DocCard icon="pi-credit-card" title="PAN Card" subtitle="Permanent Account Number" items={nationalDocs.pan} />
                                    </div>
                                )}
                                {nationalDocs.aadhaar && (
                                    <div className="col-12 md:col-6 lg:col-4">
                                        <DocCard icon="pi-id-card" title="Aadhaar" subtitle="Unique Identification" items={nationalDocs.aadhaar} />
                                    </div>
                                )}
                                {nationalDocs.voter_id && (
                                    <div className="col-12 md:col-6 lg:col-4">
                                        <DocCard icon="pi-users" title="Voter ID" subtitle="Electoral Photo ID" items={nationalDocs.voter_id} />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                    )}

                    {personal && (
                        <TabPanel header={tabHeader('pi-user', 'Personal Information')}>
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <Card className="h-full">
                                        <SectionLabel icon="pi-user">Basic Details</SectionLabel>
                                        <div className="flex flex-column gap-2">
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm text-600">Full name</span>
                                                <span className="text-sm font-semibold text-900">{personal.full_name || personal.name || 'N/A'}</span>
                                            </div>
                                            {personal.first_name && (
                                                <div className="flex align-items-center justify-content-between">
                                                    <span className="text-sm text-600">First name</span>
                                                    <span className="text-sm font-semibold text-900">{personal.first_name}</span>
                                                </div>
                                            )}
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm text-600">Gender</span>
                                                <Tag
                                                    severity={personal.gender?.toLowerCase() === 'male' ? 'info' : personal.gender?.toLowerCase() === 'female' ? 'danger' : 'warning'}
                                                    value={personal.gender || 'N/A'}
                                                    className="text-xs"
                                                />
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                                <div className="col-12 lg:col-6">
                                    <Card className="h-full">
                                        <SectionLabel icon="pi-calendar">Age & Birth Details</SectionLabel>
                                        <div className="flex flex-column gap-2">
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm text-600">Age</span>
                                                <span className="text-sm font-semibold text-900">
                                                    {personal.age || 'N/A'}{personal.age && <span className="text-xs text-500"> years</span>}
                                                </span>
                                            </div>
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm text-600">Date of birth</span>
                                                <span className="text-sm font-semibold text-900">{getValue(personal.date_of_birth)}</span>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                                {personal.income && (
                                    <div className="col-12">
                                        <Card>
                                            <div className="flex align-items-center justify-content-between gap-3">
                                                <div className="flex align-items-center gap-2">
                                                    <i className="pi pi-wallet text-primary text-lg" aria-hidden="true" />
                                                    <div>
                                                        <p className="text-xs text-600 m-0">Annual income</p>
                                                        <p className="text-base font-bold text-900 m-0">{inr(personal.income)}</p>
                                                    </div>
                                                </div>
                                                <Tag severity="success" value="Reported" className="text-xs" />
                                            </div>
                                        </Card>
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                    )}

                    {phones.length > 0 && (
                        <TabPanel header={tabHeader('pi-phone', 'Contact Information', phones.length)}>
                            <div className="grid">
                                {phones.map((phone, i) => (
                                    <div key={i} className="col-12 md:col-6 lg:col-4">
                                        <PhoneCard phone={phone} />
                                    </div>
                                ))}
                            </div>
                        </TabPanel>
                    )}

                    {emails.length > 0 && (
                        <TabPanel header={tabHeader('pi-envelope', 'Email Addresses', emails.length)}>
                            <div className="grid">
                                {emails.map((email, i) => (
                                    <div key={i} className="col-12 md:col-6 lg:col-4">
                                        <EmailCard email={email} />
                                    </div>
                                ))}
                            </div>
                        </TabPanel>
                    )}

                    {addresses.length > 0 && (
                        <TabPanel header={tabHeader('pi-map-marker', 'Address History', addresses.length)}>
                            <div className="grid">
                                {addresses.map((address, i) => (
                                    <div key={i} className="col-12 lg:col-6">
                                        <AddressCard address={address} />
                                    </div>
                                ))}
                            </div>
                        </TabPanel>
                    )}

                    {enquiries.length > 0 && (
                        <TabPanel header={
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-history" aria-hidden="true" />
                                <span className="font-medium">Enquiry History</span>
                                <Chip label={`${enquiries.length}`} className="text-xs ml-1" />
                            </span>
                        }>
                            <Message
                                severity="info"
                                text="Multiple enquiries in a short period may impact your credit score"
                                className="w-full mb-3"
                            />
                            <EnquiryTable enquiries={enquiries} />
                        </TabPanel>
                    )}
                </TabView>
            </Card>

            {pdfPasswordHint && (
                <div className="mt-3">
                    <Message severity="info" text={`PDF password hint: ${pdfPasswordHint}`} className="w-full" />
                </div>
            )}

            <Dialog
                header={`Payment History — ${selectedAccountNumber || ''}`}
                visible={paymentHistoryModal}
                onHide={() => {
                    setPaymentHistoryModal(false);
                    setSelectedAccountHistory(null);
                    setSelectedAccountNumber(null);
                }}
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '900px' }}
            >
                {selectedAccountHistory && <PaymentHistoryView historyData={selectedAccountHistory} />}
            </Dialog>
        </ProtectedContent>
    );
};

export default EquifaxOutputComponent;
